import React, { SyntheticEvent, useEffect } from 'react';
import {
  AppBar, Toolbar, makeStyles, Tabs, Tab, Button, Grid, Popover,
  CardContent, CardActions, CardHeader, Avatar, Typography,
} from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector, useDispatch } from 'react-redux';
import pluralize from 'pluralize';

import logoWithName from '../assets/logo-with-name.svg';
import {
  Auth0Context, IS_ADMIN_KEY, ORGANIZATION_ID_KEY,
} from '../utils/auth0';
import {
  selectOrganizations, loadMyOrganization,
} from '../store/slices/organizationsSlice';

export default function LimberAppBar() {
  const classes = useStyles();

  const { isAuthenticated, user } = useAuth0() as Auth0Context;

  const location = useLocation();
  const history = useHistory();

  return (
    <AppBar position="fixed">
      <Toolbar>
        <Grid container alignItems="center">
          <Grid item xs={3}>
            <Link to="/">
              <img src={logoWithName} className={classes.logo} alt="Limber" />
            </Link>
          </Grid>
          <Grid item xs={6}>
            {isAuthenticated && user != null && (
              <Tabs
                value={location.pathname}
                onChange={(event, value) => history.push(value)}
                className={classes.tabs}
                centered
              >
                {user[IS_ADMIN_KEY] ? [
                  <Tab
                    label="Population Analytics"
                    value="/statistics"
                    className={classes.tabs}
                  />,
                  <Tab
                    label="User Reports"
                    value="/user-reports"
                    className={classes.tabs}
                  />,
                  <Tab
                    label="Physicians"
                    value="/physicians"
                    className={classes.tabs}
                  />,
                ] : [
                  <Tab
                    label="Dashboard"
                    value="/statistics"
                    className={classes.tabs}
                  />,
                  <Tab
                    label="Patients"
                    value="/patients"
                    className={classes.tabs}
                  />,
                  <Tab
                    label="Outcome Measures"
                    value="/outcome-measures"
                    className={classes.tabs}
                  />,
                ]}
              </Tabs>
            )}
          </Grid>
          <Grid item xs={3} className={classes.right}>
            <UserMenu />
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  );
}

function UserMenu() {
  const {
    isAuthenticated, user, getAccessTokenSilently,
  } = useAuth0() as Auth0Context;

  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const open = Boolean(anchorEl);

  const handleMenu = (event: SyntheticEvent) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const classes = useStyles();
  const dispatch = useDispatch();
  const { myOrganization } = useSelector(selectOrganizations);

  useEffect(() => {
    if (
      myOrganization == null
      && user != null
      && user[ORGANIZATION_ID_KEY] != null
    ) {
      dispatch(loadMyOrganization(getAccessTokenSilently));
    }
  }, [user]);

  if (!isAuthenticated || user == null) return null;

  const organizationCodes = myOrganization?.organizationCodes || [];

  return (
    <div>
      <Button
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        endIcon={<AccountCircleIcon />}
      >
        {myOrganization?.name || user.name}
      </Button>
      <Popover
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
      >
        <CardHeader
          avatar={
            <Avatar className={classes.avatar}>
              {user.name[0].toUpperCase()}
            </Avatar>
          }
          title={user?.name}
          subheader={myOrganization?.name}
        />
        <CardActions>
          {!user[IS_ADMIN_KEY] && (
            <Button
              onClick={handleClose}
              component="a"
              href="mailto:support@limberhealth.com"
            >
              Contact Limber
            </Button>
          )}
          <Button
            onClick={handleClose}
            component={Link}
            to="/logout"
            className={classes.logoutButton}
          >
            Logout
          </Button>
        </CardActions>
      </Popover>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  logo: {
    height: theme.spacing(6),
  },
  tabs: {
    ...theme.mixins.toolbar,
  },
  right: {
    textAlign: 'right',
  },
  avatar: {
    backgroundColor: theme.palette.secondary.main,
  },
  logoutButton: {
    marginLeft: 'auto',
  },
}));
